import {
  memo,
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import { InputSearch, Loader, NewTable } from 'components';
import { Select } from 'components/NewSelect';
import { EmptyGoalsDataContainer, FilterTableContainer, LoaderContainer } from './styles';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ResearchIcon } from '../../../../../assets/img/research.svg';
import { GetGoalsResponse, GoalTableItem, Option } from './types';
import { getYearOptionsByGoals, parseEnergyGoalsDataTable } from './helpers';
import { useDebounce } from '~/hooks';

interface EnergyConsumptionTableProps {
  data: GetGoalsResponse;
  loading: boolean;
  onDelete: (row: GoalTableItem) => void;
  onEdit: (row: GoalTableItem) => void;
}

type TableProps = Pick<EnergyConsumptionTableProps, 'onDelete' | 'onEdit'> & {
  data: GoalTableItem[];
  loading: boolean;
};

function EmptyGoalsData(): ReactElement {
  const { t } = useTranslation();

  return (
    <EmptyGoalsDataContainer>
      <div>
        <ResearchIcon />
      </div>
      <div>
        <h3>{t('nenhumaMetaAnoSelecionado')}</h3>
        <p>{t('cadastreMetasPlataformaEnvieViaPlanilha')}</p>
      </div>
    </EmptyGoalsDataContainer>
  );
}

const TableComponent = memo(({
  data,
  loading,
  onDelete,
  onEdit,
}: TableProps): ReactElement => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  const columns = useMemo(() => (
    [
      {
        name: 'unit',
        value: t('unidade'),
        accessor: 'UNIT_NAME',
        width: '48%',
        render: (props) => (props.UNIT_NAME ?? '-'),
      },
      {
        name: 'updated_at',
        value: t('ultimoLancamentoEm'),
        accessor: 'UPDATED_AT',
        width: '48%',
        render: (props) => props.UPDATED_AT ?? '-',
      },
    ]
  ), []);

  if (loading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  if (data.length === 0) {
    return <EmptyGoalsData />;
  }

  return (
    <div>
      <NewTable
        actions={{
          onDelete,
          onEdit,
        }}
        columns={columns}
        data={data}
        pageSize={10}
        styledPagination
        noSearchBar
        containerStyle={{
          marginTop: '-20px',
        }}
        variant="striped"
        verifyMobile
      />
    </div>
  );
});

export function ConsumptionTable({
  data,
  loading,
  onEdit,
  onDelete,
}: Readonly<EnergyConsumptionTableProps>): ReactElement {
  const [searchValue, setSearchValue] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [yearOptions, setYearOptions] = useState<{ label: string, value: string }[]>([]);
  const debouncedLoading = useDebounce(loading, 200);
  const debouncedYear = useDebounce(year, 200);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const { t } = useTranslation();

  const filteredDataTable = useMemo(
    (): GoalTableItem[] => parseEnergyGoalsDataTable(
      data,
      debouncedYear,
      debouncedSearchValue.toLowerCase(),
    ),
    [data, debouncedSearchValue, debouncedYear],
  );

  const onSelectYear = useCallback((option: Option): void => {
    setYear(option.value);
  }, []);

  const onChangeSearch = useCallback((value: string): void => {
    setSearchValue(value);
  }, []);

  useEffect(() => {
    const currentYear = new Date().getFullYear().toString();
    setYearOptions(getYearOptionsByGoals(data, currentYear));
    setYear(currentYear);
  }, [data]);

  return (
    <>
      <FilterTableContainer>
        <div>
          <InputSearch
            id="search"
            name="search"
            placeholder={t('pesquisarUnidade')}
            value={searchValue}
            onChange={(event) => onChangeSearch(event.target.value)}
            style={{ width: '263px' }}
          />
        </div>
        <Select
          label={t('ano')}
          style={{ width: '224px' }}
          options={yearOptions}
          value={year}
          placeholder={t('selecionar')}
          onSelect={onSelectYear}
          hideSelected
        />
      </FilterTableContainer>
      <TableComponent
        data={filteredDataTable}
        loading={loading || debouncedLoading}
        onDelete={onDelete}
        onEdit={onEdit}
      />
    </>
  );
}

export const EnergyConsumptionTable = memo(ConsumptionTable);
